import { ChangeEvent, FC } from "react";
import { Control, useController } from "react-hook-form";
import styled from "styled-components";

import { Input, Label } from "@/components/Forms";
import { ensureStringContainsPositiveDigitsOnly } from "@/utils/helpers";
import { FormDataT } from "./DistributionRepostsForm";
import Text from "@/components/Text";
import ValidationMessage from "@/components/Forms/ValidationMessage";

const InputGroup = styled.div`
  position: relative;
`;

const InputLabel = styled(Text)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 40px;
  height: 24px;
  margin: auto 0;
`;

const Buttons = styled.div`
  position: absolute;
  width: 46px;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: 0.2s ease-out border-color;
`;

const ArrowButton = styled.button`
  width: 8px;
  height: 5px;
  padding: 3px;
  box-sizing: content-box;
  display: flex;
`;

const StyledInput = styled(Input)`
  &:focus,
  &:hover {
    + ${Buttons} {
      border-color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

interface Props {
  name:
    | `conditions.${number}.female_percentage`
    | `conditions.${number}.male_percentage`;
  control: Control<FormDataT>;
  label: string;
}

const AudienceSegmentationField: FC<Props> = ({ name, control, label }) => {
  const {
    field: { onChange, value, onBlur },
    fieldState: { error },
  } = useController<
    FormDataT,
    | `conditions.${number}.female_percentage`
    | `conditions.${number}.male_percentage`
  >({
    name,
    control,
  });

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!ensureStringContainsPositiveDigitsOnly(e.target.value)) return;

    onChange(e.target.value);
  };

  const increment = () => {
    const currentValue = value || "0";

    const newValue = parseInt(currentValue) + 1;

    onChange(newValue.toString());
  };

  const decrement = () => {
    const currentValue = parseInt(value || "0");

    const newValue = currentValue > 1 ? currentValue - 1 : "";

    onChange(newValue.toString());
  };

  return (
    <div>
      <Label $color="gray">{label}</Label>
      <InputGroup>
        <StyledInput
          onChange={onInputChange}
          name="min"
          value={value}
          onBlur={onBlur}
          placeholder="0"
        />
        <Buttons>
          <ArrowButton type="button" onClick={() => increment()}>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 0L0 5H8L4 0Z" fill="currentColor" />
            </svg>
          </ArrowButton>
          <ArrowButton type="button" onClick={() => decrement()}>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 5L8 0H0L4 5Z" fill="currentColor" />
            </svg>
          </ArrowButton>
        </Buttons>
        <InputLabel $color="gray" $type="caption" as="span">
          %
        </InputLabel>
      </InputGroup>
      {error && <ValidationMessage>{error.message}</ValidationMessage>}
    </div>
  );
};

export default AudienceSegmentationField;
